import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { t } from "@/i18n";
import { Button, Card, Field, Input, Select, Badge, EmptyState } from "@/components/ui";
import {
  useAccountLinks,
  useInviteAccount,
  useAcceptLink,
  useRemoveLink,
} from "@/lib/queries";

export const Route = createFileRoute("/app/links")({
  component: LinkedAccountsPage,
});

function LinkedAccountsPage() {
  const { data, isLoading } = useAccountLinks();
  const invite = useInviteAccount();
  const accept = useAcceptLink();
  const remove = useRemoveLink();

  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"editor" | "viewer">("editor");
  const [error, setError] = useState("");
  const [copied, setCopied] = useState("");

  const outgoing = data?.outgoing ?? [];
  const incoming = data?.incoming ?? [];

  async function submitInvite() {
    setError("");
    if (!email.trim()) {
      setError(t("links.emailRequired"));
      return;
    }
    try {
      await invite.mutateAsync({ email: email.trim(), role });
      setEmail("");
    } catch (e) {
      setError(e instanceof Error ? e.message : "error");
    }
  }

  function copyInviteLink(linkId: string, token: string) {
    const url = `${window.location.origin}/sign-in?invite=${linkId}`;
    void navigator.clipboard?.writeText(url);
    setCopied(token);
    setTimeout(() => setCopied(""), 2000);
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">{t("links.title")}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t("links.subtitle")}</p>
      </div>

      <Card className="max-w-2xl space-y-4 p-6">
        <h2 className="text-sm font-semibold">{t("links.inviteTitle")}</h2>
        <p className="text-sm text-muted-foreground">{t("links.inviteHint")}</p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_auto_auto] sm:items-end">
          <Field label={t("links.emailLabel")}>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("links.emailPlaceholder")}
            />
          </Field>
          <Field label={t("links.role")}>
            <Select value={role} onChange={(e) => setRole(e.target.value as "editor" | "viewer")}>
              <option value="editor">{t("links.roleEditor")}</option>
              <option value="viewer">{t("links.roleViewer")}</option>
            </Select>
          </Field>
          <Button disabled={invite.isPending} onClick={() => void submitInvite()}>
            {invite.isPending ? t("common.saving") : t("links.invite")}
          </Button>
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
      </Card>

      <Card className="space-y-3 p-6">
        <h2 className="text-sm font-semibold">{t("links.outgoing")}</h2>
        {isLoading ? null : outgoing.length === 0 ? (
          <EmptyState>{t("links.emptyOutgoing")}</EmptyState>
        ) : (
          <ul className="divide-y">
            {outgoing.map((l) => (
              <li key={l.id} className="flex flex-wrap items-center gap-3 py-2">
                <span className="font-medium">{l.invitedEmail}</span>
                <Badge tone={l.role === "editor" ? "blue" : "muted"}>
                  {l.role === "editor" ? t("links.roleEditor") : t("links.roleViewer")}
                </Badge>
                {l.status === "pending" ? (
                  <Badge tone="amber">{t("links.pending")}</Badge>
                ) : (
                  <Badge tone="green">{t("links.accepted")}</Badge>
                )}
                {l.status === "accepted" && l.linkedEmail && (
                  <span className="text-sm text-muted-foreground">→ {l.linkedEmail}</span>
                )}
                {l.status === "pending" && (
                  <Button
                    variant="ghost"
                    onClick={() => copyInviteLink(l.id, l.inviteToken)}
                  >
                    {copied === l.inviteToken ? `✓ ${t("links.copied")}` : t("links.copyLink")}
                  </Button>
                )}
                <Button
                  variant="ghost"
                  disabled={remove.isPending}
                  onClick={() => remove.mutate(l.id)}
                >
                  {t("links.revoke")}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card className="space-y-3 p-6">
        <h2 className="text-sm font-semibold">{t("links.incoming")}</h2>
        {isLoading ? null : incoming.length === 0 ? (
          <EmptyState>{t("links.emptyIncoming")}</EmptyState>
        ) : (
          <ul className="divide-y">
            {incoming.map((l) => (
              <li key={l.id} className="flex flex-wrap items-center gap-3 py-2">
                <span className="font-medium">
                  {l.ownerName || l.ownerEmail}
                  {l.ownerEmail && l.ownerName && (
                    <span className="ml-2 text-sm text-muted-foreground">{l.ownerEmail}</span>
                  )}
                </span>
                {l.status === "pending" ? (
                  <>
                    <Badge tone="amber">{t("links.pending")}</Badge>
                    <Button disabled={accept.isPending} onClick={() => accept.mutate(l.id)}>
                      {t("links.accept")}
                    </Button>
                    <Button
                      variant="ghost"
                      disabled={remove.isPending}
                      onClick={() => remove.mutate(l.id)}
                    >
                      {t("links.decline")}
                    </Button>
                  </>
                ) : (
                  <>
                    <Badge tone="green">{t("links.accepted")}</Badge>
                    <Button
                      variant="ghost"
                      disabled={remove.isPending}
                      onClick={() => remove.mutate(l.id)}
                    >
                      {t("links.unlink")}
                    </Button>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
